import Store from 'Store/.';
import { Dispatch } from 'redux';
import Api from 'Helpers/Api';

import { 
  OrderAction, 
  setOrder, 
  setOrderExpireDate,
  setIsFetchingOrder, 
  setIsFetchingPayment, 
  setFetchOrderFailed, 
  setFetchPaymentFailed, 
  setPaymentResult, 
  setDiscountFailed,
  addSkanetrafikenTravel, 
  removeSkanetrafikenTravel, 
  clearOrder,
  setDiscountResult,
  setIsFetchingDiscount, 
} from './actions';
import moment from 'moment';

export const fetchCurrentOrder = () => {
  return async (dispatch: Dispatch<OrderAction>): Promise<void> => {
    const { isFetchingOrder } = Store.getState().order;

    if (isFetchingOrder) {
      return null;
    }

    dispatch(setIsFetchingOrder());

    try {
      const response = await Api.get('CurrentOrder');

      if (response.status === 200) {
        const order: Julius.Website.OrderViewModel = await response.json();
        dispatch(setOrder(order));
      } else {
        dispatch(setFetchOrderFailed());
      }
    } catch (e) {
      dispatch(setFetchOrderFailed());
    }
  }
};

export const createOrder = (orderRequest: Julius.Website.OrderRequestViewModel) => {
  return async (dispatch: Dispatch<OrderAction>): Promise<void> => {
    const { isFetchingOrder } = Store.getState().order;

    if (isFetchingOrder) {
      return null;
    }

    dispatch(setIsFetchingOrder());

    try {

      const response = await Api.post('OrderProposal', orderRequest);

      if (response.status === 200) {
        const order: Julius.Website.OrderViewModel = await response.json();
        dispatch(setOrder(order));
      } else {
        dispatch(setFetchOrderFailed());
      }
    } catch (e) {
      dispatch(setFetchOrderFailed());
    }
  }
};

export const fetchOrderWithCampaign = (campaignCode: string) => {
  return async (dispatch: Dispatch<OrderAction>): Promise<void> => {
    const { 
      isFetchingDiscount, 
      orderTotalPrice, 
      selectedVouchers,
    } = Store.getState().order;

    if (isFetchingDiscount || !campaignCode) {
      return null;
    }

    dispatch(setIsFetchingDiscount());
    
    try {
      const response = await Api.get('OrderWithCampaign', `code=${ campaignCode }`);
      const order: Julius.Website.OrderViewModel = await response.json();
      if (
        response.status === 200 && order &&
        (order.orderTotalPrice !== orderTotalPrice ||
        (order.selectedVouchers && order.selectedVouchers.length !== selectedVouchers.length))
      ) {
        dispatch(setDiscountResult(order));
      } else {
        dispatch(setDiscountFailed(order));
      }
    } catch (e) {
      dispatch(setDiscountFailed());
    }
  }
};

export const fetchOrderSummary = (orderDetails: Julius.Website.OrderDetailsViewModel) => {
  return async (dispatch: Dispatch<OrderAction>): Promise<void> => {
    const { isFetchingOrder } = Store.getState().order;

    if (isFetchingOrder) {
      return null;
    }

    dispatch(setIsFetchingOrder());

    try {
      const response = await Api.post('OrderSummary', orderDetails);

      if (response.status === 200) {
        const order: Julius.Website.OrderViewModel = await response.json();
        dispatch(setOrder(order));
      } else {
        dispatch(setFetchOrderFailed());
      }
    } catch (e) {
      dispatch(setFetchOrderFailed());
    }
  }
};

export const fetchPaymentResult = (returnPath: string) => {
  return async (dispatch: Dispatch<OrderAction>): Promise<void> => {
    const { isFetchingPayment, selectedPaymentCode } = Store.getState().order;

    if (isFetchingPayment || !selectedPaymentCode) {
      return null;
    }

    dispatch(setIsFetchingPayment());

    try {
      const returnUrl = `${ process.env.REACT_APP_API_URL }${ returnPath }`;
      const queryParams = `paymentCode=${ selectedPaymentCode }&returnUrl=${ returnUrl }`;
      const response = await Api.get('Payment', queryParams);

      if (response.status === 200) {
        const paymentResult: Julius.Website.PaymentResultViewModel = await response.json();
        dispatch(setPaymentResult(paymentResult));
      } else {
        dispatch(setFetchPaymentFailed(true));
      }
    } catch (e) {
      dispatch(setFetchPaymentFailed(true));
    }
  }
};

export const fetchOrderConfirmation = (transactionID?: string, responseCode?: string, isAutomaticPolling: boolean = false) => {
  return async (dispatch: Dispatch<OrderAction>): Promise<void> => {
    const { isFetchingOrder } = Store.getState().order;

    if (isFetchingOrder) {
      return null;
    }

    dispatch(setIsFetchingOrder());

    try {
      const queryParams = `transactionID=${ transactionID }&responseCode=${ responseCode }&isAutomaticPolling=${ isAutomaticPolling }`;
      const response = await Api.get('OrderConfirmation', queryParams);
      const order: Julius.Website.OrderViewModel = await response.json();

      if (response.status === 200) {
        dispatch(setOrder(order));
      } else {
        dispatch(setFetchOrderFailed());
      } 
    } catch (e) {
      dispatch(setFetchOrderFailed());
    }
  }
};

export const fetchClearOrder = () => {
  return async (dispatch: Dispatch<OrderAction>): Promise<void> => {
    try {
      const response = await Api.get('ClearOrder');
      const isCleared: boolean = await response.json();
      dispatch(clearOrder());
    } catch (e) {
      dispatch(clearOrder());
    }
  }
};

export const fetchOrderExpireDate = () => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await Api.get('OrderExpireDate');
      let expireDate = await response.json();

      if (expireDate !== null) {
        expireDate = moment(expireDate).toDate();
        dispatch(setOrderExpireDate(expireDate));
      }
    } catch (e) {
      dispatch(setOrderExpireDate(null));
    }
  };
};

export const fetchConfirmationPDF = (orderID: number) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await Api.get('ReservationConfirmation', `orderId=${ orderID }`);
      const blob = await response.blob();
      triggerDownloadPDF(blob);
    } catch (e) {
      dispatch(setFetchOrderFailed());
    }
  };
};

export const fetchTicketPDF = (orderID: number) => {
  return async (dispatch: Dispatch): Promise<void> => {
    try {
      const response = await Api.get('PrintAtHome', `orderId=${ orderID }`);
      const blob = await response.blob();
      triggerDownloadPDF(blob);
    } catch (e) {
      dispatch(setFetchOrderFailed());
    }
  };
};

const triggerDownloadPDF = (blob: Blob) => {
  const pdf = new Blob([blob], { type: "application/pdf" })

  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(pdf);
    return;
  } 

  const url = window.URL.createObjectURL(pdf);
  const link = document.createElement('a');
  link.setAttribute("type", "hidden");
  document.body.appendChild(link);
  link.download="JuliusBiljettservice_Biljett.pdf";
  link.href = url;
  link.click();

  setTimeout(() => {
    // For Firefox it is necessary to delay revoking the ObjectURL
    window.URL.revokeObjectURL(url);
    link.remove();
  }, 100 );
};

export const createTravelReservation = (request: Julius.Website.TravelRouteViewModel) => {
  return async (dispatch: Dispatch<OrderAction>): Promise<void> => {

    try {
      const response = await Api.post('TravelReservation', request);
      const newTravel: Julius.Website.TravelRouteViewModel = await response.json();

      if (response.status === 200 && newTravel) {
        dispatch(addSkanetrafikenTravel(newTravel));
      }
    } catch (e) {
      // dispatch(setFetchFailed());
    }
  };
};

export const removeTravelReservation = (request: Julius.Website.TravelRouteViewModel) => {
  return async (dispatch: Dispatch<OrderAction>): Promise<void> => {

    try {
      const response = await Api.delete('TravelReservation', null, request);
      const removedTravel: Julius.Website.TravelRouteViewModel = await response.json();

      if (response.status === 200 && removedTravel) {
        dispatch(removeSkanetrafikenTravel(removedTravel));
      }
    } catch (e) {
      // dispatch(setFetchFailed());
    }
  };
};


// WEBPACK FOOTER //
// ./src/store/Order/thunks.tsx